import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatAPI, conversationAPI, voiceAPI, billingAPI } from '../api';
import { useSettings } from '../contexts/SettingsContext';
import { useConversations } from '../contexts/ConversationsContext';
import MarkdownRenderer from './MarkdownRenderer';
import MediaMessage from './media/MediaMessage';
import VoiceRecordingInput from './VoiceRecordingInput';
import CombinedMessage from './CombinedMessage';
import '../styles/whatsapp-chat.css';

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Read an image File into { mediaType, data } where data is raw base64
// (no data: prefix) — the shape the vision endpoint expects.
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const comma = result.indexOf(',');
    const meta = result.slice(0, comma);
    const data = result.slice(comma + 1);
    const mediaType = (meta.match(/data:(.*?);base64/) || [])[1] || file.type || 'image/png';
    resolve({ mediaType, data });
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Text bubble with a typewriter effect for streaming AI replies.
// IMPORTANT: defined at module scope — defining it inside Chat would give it a
// new identity on every render, making React unmount/remount every bubble on
// each streamed token (which kills the animation and thrashes the DOM).
const TextMessage = React.memo(({
  message, isSender, onType, onSpeak, speaking, ttsAvailable,
  onCopy, copied, onRegenerate, canRegenerate,
  isEditing, onStartEdit, onSaveEdit, onCancelEdit
}) => {
  const [displayedText, setDisplayedText] = useState(message.text || '');
  const [editText, setEditText] = useState(message.text || '');
  const textRef = useRef(message.text || '');
  const isStreamingRef = useRef(message.streaming);
  const currentIndexRef = useRef((message.text || '').length);
  const onTypeRef = useRef(onType);

  useEffect(() => {
    textRef.current = message.text || '';
    isStreamingRef.current = message.streaming;
    onTypeRef.current = onType;
  }, [message.text, message.streaming, onType]);

  // Seed the edit box when entering edit mode
  useEffect(() => {
    if (isEditing) setEditText(message.text || '');
  }, [isEditing, message.text]);

  // Typewriter effect for streaming AI messages
  useEffect(() => {
    if (isSender || !isStreamingRef.current) {
      setDisplayedText(textRef.current || '');
      currentIndexRef.current = (textRef.current || '').length;
      return;
    }

    let timeoutId;
    const typeNextChar = () => {
      const targetText = textRef.current;
      const currentLen = currentIndexRef.current;
      const targetLen = targetText.length;

      if (currentLen < targetLen) {
        // Adapt speed to the backlog so the animation never lags far
        // behind the real stream
        const backlog = targetLen - currentLen;
        let charsToAppend = 1;
        let delay = 20;

        if (backlog > 120) {
          charsToAppend = 12;
          delay = 5;
        } else if (backlog > 40) {
          charsToAppend = 4;
          delay = 5;
        } else if (backlog > 20) {
          charsToAppend = 2;
          delay = 10;
        }

        const nextLen = Math.min(currentLen + charsToAppend, targetLen);
        currentIndexRef.current = nextLen;
        setDisplayedText(targetText.substring(0, nextLen));

        if (onTypeRef.current) onTypeRef.current();

        timeoutId = setTimeout(typeNextChar, delay);
      } else if (isStreamingRef.current) {
        // Caught up — poll for more incoming stream data
        timeoutId = setTimeout(typeNextChar, 50);
      }
    };

    typeNextChar();

    return () => clearTimeout(timeoutId);
  }, [isSender, message.id]);

  // Fast-forward to the final text once streaming completes
  useEffect(() => {
    if (!message.streaming) {
      setDisplayedText(message.text || '');
      currentIndexRef.current = (message.text || '').length;
      if (onTypeRef.current) onTypeRef.current();
    }
  }, [message.streaming, message.text]);

  const hasText = (message.text || '').trim();
  const showActions = !message.streaming && hasText;
  // Speaker button only for AI text replies once they've finished streaming
  const showSpeaker = !isSender && ttsAvailable && showActions;
  // Render markdown for completed AI replies; keep plain text while streaming
  // (typewriter) and for user messages (they type plain text).
  const renderMarkdown = !isSender && !message.streaming && hasText;

  // Inline edit mode for a user message
  if (isEditing) {
    return (
      <div className={`message ${isSender ? 'message-sender' : 'message-receiver'}`}>
        <div className="message-content message-editing">
          <textarea
            className="edit-textarea"
            value={editText}
            autoFocus
            rows={Math.min(6, (editText.match(/\n/g)?.length || 0) + 1)}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSaveEdit(message.id, editText); }
              if (e.key === 'Escape') onCancelEdit();
            }}
          />
          <div className="edit-actions">
            <button className="edit-cancel" onClick={onCancelEdit}>Cancel</button>
            <button className="edit-save" onClick={() => onSaveEdit(message.id, editText)}>Save &amp; Send</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`message ${isSender ? 'message-sender' : 'message-receiver'}`}>
      <div className="message-content">
        <div className="message-text">
          {renderMarkdown ? (
            <MarkdownRenderer text={message.text} />
          ) : (
            <>
              {displayedText}
              {message.streaming && <span className="streaming-cursor" />}
            </>
          )}
        </div>
        <div className="message-footer">
          <span className="message-time">{formatTime(message.timestamp)}</span>
          <div className="msg-actions">
            {showActions && (
              <button
                className="msg-action-btn"
                title={copied ? 'Copied!' : 'Copy'}
                onClick={() => onCopy(message.text, message.id)}
              >
                {copied ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                )}
              </button>
            )}
            {showSpeaker && (
              <button
                className="msg-action-btn"
                title={speaking ? 'Stop' : 'Play voice'}
                onClick={() => onSpeak && onSpeak(message.text, message.id)}
              >
                {speaking ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5" /></svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 010 7M19 5a9 9 0 010 14" /></svg>
                )}
              </button>
            )}
            {!isSender && canRegenerate && showActions && (
              <button
                className="msg-action-btn"
                title="Regenerate"
                onClick={() => onRegenerate(message.id)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>
              </button>
            )}
            {isSender && hasText && (
              <button
                className="msg-action-btn"
                title="Edit & resend"
                onClick={() => onStartEdit(message.id)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState('');
  const { settings } = useSettings();
  const { currentId, navSeq, createConversation, saveConversation } = useConversations();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // Only auto-scroll when the user is already near the bottom,
  // so reading older messages is never interrupted
  const isNearBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  // Chat-history persistence bookkeeping
  const loadedConvIdRef = useRef(null);   // which saved conversation is on screen
  const creatingRef = useRef(false);      // guard against double-create on first message
  const skipNextSaveRef = useRef(false);  // skip the auto-save right after a load
  const saveTimerRef = useRef(null);
  // Voice mode (admin-controlled per user): STT mic + TTS spoken replies
  const voiceEnabled = !!settings?.voice?.enabled;
  const ttsAvailable = voiceEnabled && !!settings?.voice?.ttsAvailable;
  const [voiceOn, setVoiceOn] = useState(false);       // auto-speak AI replies
  const [speakingId, setSpeakingId] = useState(null);  // which message is playing
  const audioRef = useRef(null);
  const lastAutoSpokenRef = useRef(null);
  // Message actions
  const abortRef = useRef(null);                 // AbortController for the active AI stream
  const [editingId, setEditingId] = useState(null);   // user message being edited
  const [copiedId, setCopiedId] = useState(null);     // brief "copied!" feedback

  // Subscription banner. Only self-serve accounts ever have a state worth
  // showing — admin-created users come back as 'none' and see nothing.
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    let cancelled = false;
    billingAPI.me()
      .then(data => { if (!cancelled) setBilling(data); })
      // A billing hiccup must never block the chat itself — no banner is a
      // better failure than an error screen over a working product.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Which cap stopped the last message ('daily' | 'monthly' | null), so the
  // composer can grow an explanatory bar instead of the user getting a dead
  // error bubble with nothing to do.
  const [limitScope, setLimitScope] = useState(null);

  // Both ways a message can be refused for money reasons. Returns true when it
  // handled the error, so the caller skips the generic error bubble.
  const handleIfSubscriptionRequired = (error) => {
    const code = error?.code || error?.response?.data?.code;
    const status = error?.status || error?.response?.status;

    // Trial or subscription over — the plans screen is the only way forward.
    if (code === 'SUBSCRIPTION_REQUIRED' || status === 402) {
      navigate('/subscribe');
      return true;
    }
    // An included allowance is spent. Deliberately NOT a redirect: the user is
    // mid-conversation and topping up should feel like a small detour, not
    // being thrown out of the app.
    if (code === 'LIMIT_REACHED' || status === 429) {
      const scope = error?.scope || error?.response?.data?.scope;
      setLimitScope(scope === 'daily' ? 'daily' : 'monthly');
      return true;
    }
    return false;
  };

  const trialDaysLeft = billing?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(billing.trialEndsAt) - Date.now()) / (24 * 3600 * 1000)))
    : null;

  // Webhooks + AI option for the "workflow" dropdown (memoized so the
  // initial-selection effect doesn't re-run on every render)
  const { webhooks, chatTargets } = useMemo(() => {
    const hooks = settings?.webhooks && settings.webhooks.length > 0
      ? settings.webhooks.filter(w => w.url && w.url.trim() !== '')
      : [
          { id: '1', name: settings?.webhook1Name || 'WebHook-1', url: settings?.webhook1 || '' },
          { id: '2', name: settings?.webhook2Name || 'WebHook-2', url: settings?.webhook2 || '' },
          { id: '3', name: settings?.webhook3Name || 'WebHook-3', url: settings?.webhook3 || '' },
          { id: '4', name: settings?.webhook4Name || 'WebHook-4', url: settings?.webhook4 || '' }
        ].filter(w => w.url); // Only show webhooks with URLs

    // Direct AI provider option (Claude / ChatGPT / Gemini / Ollama) when assigned
    const aiConfigured = !!settings?.aiProvider?.configured;
    const aiProviderLabels = { claude: 'Claude', openai: 'ChatGPT', gemini: 'Gemini', ollama: 'Ollama' };
    // Admin can set a custom display name; blank falls back to the auto-generated label
    const customName = (settings?.aiProvider?.displayName || '').trim();
    const aiOptionName = customName
      ? `🤖 ${customName}`
      : `🤖 AI Assistant (${aiProviderLabels[settings?.aiProvider?.provider] || 'AI'})`;

    return {
      webhooks: hooks,
      chatTargets: [
        ...(aiConfigured ? [{ id: 'ai', name: aiOptionName }] : []),
        ...hooks
      ]
    };
  }, [settings]);

  // Set initial target when settings load
  useEffect(() => {
    if (chatTargets.length > 0 && !selectedWebhook) {
      setSelectedWebhook(chatTargets[0].id);
    }
  }, [chatTargets, selectedWebhook]);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // "Near bottom" = within 80px of the end
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (!isNearBottomRef.current) return; // user scrolled up — don't yank them down

    if (behavior === 'smooth') {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  };

  useEffect(() => {
    const isNewMessage = messages.length !== prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    // Smooth scroll for new bubbles; instant ("auto") for streaming text updates
    // so the view stays pinned without janky animation on every token
    scrollToBottom(isNewMessage ? 'smooth' : 'auto');
  }, [messages]);

  // Load a conversation's saved messages on explicit navigation (new chat /
  // open existing). Keyed on navSeq so that creating a conversation — which
  // updates currentId — does NOT trigger a reload that would wipe the screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentId) {
        setMessages([]);
        loadedConvIdRef.current = null;
        return;
      }
      try {
        const convo = await conversationAPI.get(currentId);
        if (cancelled) return;
        const loaded = (convo.messages || []).map((m, i) => ({
          id: `${convo._id}-${i}`,
          text: m.text || '',
          sender: m.sender,
          timestamp: m.timestamp,
          attachments: m.attachments || [],
          contentType: m.contentType || ''
        }));
        skipNextSaveRef.current = true; // don't immediately re-save what we just loaded
        loadedConvIdRef.current = currentId;
        isNearBottomRef.current = true;
        setMessages(loaded);
      } catch (error) {
        console.error('Failed to load conversation:', error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navSeq]);

  // Auto-save the conversation once an exchange completes. Creates the
  // conversation lazily on the first message, then updates it thereafter.
  useEffect(() => {
    if (isLoading) return;                       // wait until the turn finishes
    if (messages.length === 0) return;           // nothing to save
    if (messages.some(m => m.streaming)) return; // don't save mid-stream

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const payload = messages.map(m => ({
        sender: m.sender,
        text: m.text || '',
        timestamp: m.timestamp,
        attachments: (m.attachments || []).map(a => ({ name: a.name, type: a.type, size: a.size })),
        contentType: m.contentType || ''
      }));

      if (loadedConvIdRef.current) {
        saveConversation(loadedConvIdRef.current, payload);
      } else if (!creatingRef.current) {
        creatingRef.current = true;
        try {
          const firstUser = messages.find(m => m.sender === 'user' && m.text);
          const title = (firstUser?.text || 'New Chat').slice(0, 60);
          const newId = await createConversation(title, payload);
          loadedConvIdRef.current = newId;
        } catch (error) {
          console.error('Failed to create conversation:', error);
        } finally {
          creatingRef.current = false;
        }
      }
    }, 600);

    return () => clearTimeout(saveTimerRef.current);
  }, [messages, isLoading, saveConversation, createConversation]);

  // ---- Voice output (TTS) ----
  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current.src?.startsWith('blob:')) URL.revokeObjectURL(audioRef.current.src);
      audioRef.current = null;
    }
    setSpeakingId(null);
  };

  const speak = async (text, id) => {
    if (!text || !text.trim()) return;
    // Clicking the speaker of the message that's already playing stops it
    if (speakingId === id) { stopSpeaking(); return; }
    stopSpeaking();
    try {
      setSpeakingId(id);
      const url = await voiceAPI.tts(text);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); if (audioRef.current === audio) audioRef.current = null; setSpeakingId(null); };
      audio.onerror = () => { setSpeakingId(null); };
      await audio.play();
    } catch (error) {
      console.error('TTS failed:', error);
      setSpeakingId(null);
    }
  };

  // Auto-speak newly completed AI replies when voice output is on
  useEffect(() => {
    if (!voiceOn || !ttsAvailable) return;
    const last = messages[messages.length - 1];
    if (!last || last.sender !== 'ai' || last.streaming) return;
    if (!last.text || !last.text.trim()) return;
    if (lastAutoSpokenRef.current === last.id) return;
    lastAutoSpokenRef.current = last.id;
    speak(last.text, last.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, voiceOn, ttsAvailable]);

  // Stop any playback on unmount
  useEffect(() => () => stopSpeaking(), []);

  // Core: produce the assistant's reply for a user turn.
  // historyMessages = the messages BEFORE this user turn (used as AI context).
  const generateReply = async (text, attachments, historyMessages) => {
    setIsLoading(true);
    isNearBottomRef.current = true;

    try {
      let response;

      // Direct AI provider chat (no webhook) — streamed token-by-token
      if (selectedWebhook === 'ai') {
        // Vision: images go to the AI; other file types aren't supported here
        const imageAtts = (attachments || []).filter(a => a.type && a.type.startsWith('image/') && a.file);
        const nonImages = (attachments || []).filter(a => !(a.type && a.type.startsWith('image/')));
        if (nonImages.length > 0) {
          setMessages(prev => [...prev, {
            id: Date.now() + 1,
            text: 'The AI Assistant can read images only. For PDFs or other files, please select an n8n workflow.',
            sender: 'ai',
            timestamp: new Date()
          }]);
          setIsLoading(false);
          return;
        }

        let images = [];
        if (imageAtts.length > 0) {
          try {
            images = await Promise.all(imageAtts.map(a => fileToBase64(a.file)));
          } catch (error) {
            console.error('Failed to read image:', error);
          }
        }

        const history = historyMessages
          .filter(m => m.text && (!m.attachments || m.attachments.length === 0))
          .slice(-20)
          .map(m => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.text
          }));

        // Placeholder bubble that fills up as tokens stream in
        const aiMessageId = Date.now() + 1;
        setMessages(prev => [...prev, {
          id: aiMessageId,
          text: '',
          sender: 'ai',
          timestamp: new Date(),
          streaming: true
        }]);

        const controller = new AbortController();
        abortRef.current = controller;
        try {
          const result = await chatAPI.streamMessageToAI(text, history, (fullText) => {
            setMessages(prev => prev.map(m =>
              m.id === aiMessageId ? { ...m, text: fullText } : m
            ));
          }, controller.signal, images);
          setMessages(prev => prev.map(m =>
            m.id === aiMessageId
              // binaryData is set only for turns that produce a file — a
              // generated image today. Attaching it here lets the existing
              // media renderer show it without a separate code path.
              ? {
                  ...m,
                  streaming: false,
                  ...(result?.binaryData
                    ? { binaryData: result.binaryData, contentType: result.contentType }
                    : {})
                }
              : m
          ));
        } catch (streamError) {
          const aborted = controller.signal.aborted || streamError.name === 'AbortError';
          if (!aborted) console.error('AI stream error:', streamError);
          // Drop the empty placeholder bubble before leaving for the plans
          // screen — otherwise it is still sitting there when they come back.
          if (!aborted && handleIfSubscriptionRequired(streamError)) {
            setMessages(prev => prev.filter(m => m.id !== aiMessageId));
            setIsLoading(false);
            return;
          }
          setMessages(prev => prev.map(m =>
            m.id === aiMessageId
              ? {
                  ...m,
                  streaming: false,
                  text: m.text || (aborted ? '⏹️ Generation stopped.' : (streamError.message || 'Failed to get AI response. Please try again.'))
                }
              : m
          ));
        } finally {
          abortRef.current = null;
        }

        setIsLoading(false);
        return;
      } else if (attachments && attachments.length > 0) {
        // Always use multipart/form-data when there are attachments
        const chatFormData = new FormData();
        chatFormData.append('message', text || '');
        chatFormData.append('webhookNumber', selectedWebhook || webhooks[0]?.id);
        attachments.forEach((attachment) => {
          chatFormData.append('data', attachment.file, attachment.name || 'file');
        });
        response = await chatAPI.sendMessageWithFile(chatFormData);
      } else if (text.trim()) {
        response = await chatAPI.sendMessage(text, selectedWebhook || webhooks[0]?.id);
      }

      if (response) {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          text: response.reply || response.response,
          sender: 'ai',
          timestamp: new Date(),
          binaryData: response.binaryData,
          contentType: response.contentType
        }]);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error sending message:', error);
      if (handleIfSubscriptionRequired(error)) {
        setIsLoading(false);
        return;
      }
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: error.response?.data?.message || 'Failed to send message. Please try again.',
        sender: 'ai',
        timestamp: new Date()
      }]);
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (messageData) => {
    const { text, attachments } = messageData;

    if ((!text.trim() && (!attachments || attachments.length === 0)) || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: text || '',
      sender: 'user',
      timestamp: new Date(),
      attachments: attachments || []
    };

    // History for the AI = everything before this new user message
    const historyBefore = messages;
    setMessages(prev => [...prev, userMessage]);
    await generateReply(text, attachments, historyBefore);
  };

  // ---- Message actions ----

  const stopGeneration = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const copyMessage = async (text, id) => {
    const value = text || '';
    let ok = false;
    try {
      await navigator.clipboard.writeText(value);
      ok = true;
    } catch {
      // Fallback for browsers/contexts where the Clipboard API is blocked
      try {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    }
    if (ok) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(c => (c === id ? null : c)), 1500);
    }
  };

  // Regenerate an AI reply: re-run the user message that produced it
  const regenerate = (aiMessageId) => {
    if (isLoading) return;
    const aiIdx = messages.findIndex(m => m.id === aiMessageId);
    if (aiIdx < 0) return;
    // The user turn is the last user message before this AI reply
    let userIdx = -1;
    for (let i = aiIdx - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') { userIdx = i; break; }
    }
    if (userIdx < 0) return;
    const userMsg = messages[userIdx];
    const historyBefore = messages.slice(0, userIdx);
    // Keep up to and including the user message; drop the old reply(s)
    setMessages(messages.slice(0, userIdx + 1));
    generateReply(userMsg.text, userMsg.attachments || [], historyBefore);
  };

  // Edit a user message and resend from that point
  const saveEdit = (userMessageId, newText) => {
    const text = (newText || '').trim();
    setEditingId(null);
    if (!text) return;
    const idx = messages.findIndex(m => m.id === userMessageId);
    if (idx < 0) return;
    const historyBefore = messages.slice(0, idx);
    const editedUser = { ...messages[idx], text, attachments: [] };
    // Replace the message, drop everything after it
    setMessages([...historyBefore, editedUser]);
    generateReply(text, [], historyBefore);
  };

  const formatMessageForMedia = (message) => {
    if (message.binaryData && message.contentType) {
      return {
        text: message.text,
        timestamp: message.timestamp,
        media: {
          url: message.binaryData,
          mimeType: message.contentType,
          originalName: `download-${Date.now()}.${message.contentType.split('/')[1] || 'bin'}`,
          size: 0
        }
      };
    }
    return {
      text: message.text,
      timestamp: message.timestamp
    };
  };

  // A visible streaming bubble replaces the "typing..." indicator
  const hasStreamingMessage = messages.some(m => m.streaming);
  // Regenerate only appears on the most recent AI reply
  const lastAiId = [...messages].reverse().find(m => m.sender === 'ai')?.id;

  return (
    <div className="chat-container">
      {/* Trial banner — shown only while a free trial is running, so a paid or
          admin-created account never sees an upsell strip. */}
      {billing?.state === 'trialing' && (
        <div className="flex items-center justify-center gap-3 flex-wrap px-4 py-2 bg-teal-500/10 border-b border-teal-500/30 text-sm">
          <span className="text-teal-300">
            Free trial{trialDaysLeft !== null && ` — ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`}
            {billing.usage?.limit > 0 && ` · ${billing.usage.used}/${billing.usage.limit} messages used`}
          </span>
          <button
            onClick={() => navigate('/subscribe')}
            className="px-3 py-1 rounded-md bg-teal-600 hover:bg-teal-500 text-white text-xs font-medium"
          >
            Upgrade
          </button>
        </div>
      )}

      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-avatar">
            <div className="avatar-icon">🤖</div>
          </div>
          <div className="chat-info">
            <h3 className="chat-title">ROUT AI Assistant</h3>
            <p className="chat-status">Online • Ready to help</p>
          </div>

          {/* Voice output toggle — auto-speak AI replies */}
          {ttsAvailable && (
            <button
              className={`voice-toggle ${voiceOn ? 'voice-toggle-on' : ''}`}
              onClick={() => { if (voiceOn) stopSpeaking(); setVoiceOn(v => !v); }}
              title={voiceOn ? 'Voice replies: ON (tap to mute)' : 'Voice replies: OFF (tap to enable)'}
            >
              {voiceOn ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                  <path d="M15.5 8.5a5 5 0 010 7M19 5a9 9 0 010 14" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div 
        className="messages-container"
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
      >
        {messages.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-text">
              Start typing to begin the conversation...
            </div>
          </div>
        )}
        
        {messages.map((message) => {
          // Use CombinedMessage for user messages with attachments + text
          if (message.sender === 'user' && message.attachments && message.attachments.length > 0) {
            return (
              <CombinedMessage
                key={message.id}
                message={message}
                isSender={message.sender === 'user'}
              />
            );
          }
          // Use MediaMessage for AI messages with media content
          else if (message.binaryData && message.contentType) {
            return (
              <MediaMessage
                key={message.id}
                message={formatMessageForMedia(message)}
                isSender={message.sender === 'user'}
              />
            );
          } else {
            // Use TextMessage for simple text messages
            return (
              <TextMessage
                key={message.id}
                message={message}
                isSender={message.sender === 'user'}
                onType={() => scrollToBottom('auto')}
                onSpeak={speak}
                speaking={speakingId === message.id}
                ttsAvailable={ttsAvailable}
                onCopy={copyMessage}
                copied={copiedId === message.id}
                onRegenerate={regenerate}
                canRegenerate={!isLoading && message.id === lastAiId}
                isEditing={editingId === message.id}
                onStartEdit={setEditingId}
                onSaveEdit={saveEdit}
                onCancelEdit={() => setEditingId(null)}
              />
            );
          }
        })}
        
        {/* Typing indicator only until the streaming bubble takes over */}
        {isLoading && !hasStreamingMessage && (
          <div className="message message-receiver">
            <div className="message-content">
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-text">ROUT AI is typing...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Stop generation — only for the streaming AI provider */}
      {isLoading && selectedWebhook === 'ai' && (
        <div className="stop-gen-bar">
          <button className="stop-gen-btn" onClick={stopGeneration}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
            Stop generating
          </button>
        </div>
      )}

      {/* Out of messages. Sits directly above the composer — the user hit this
          wall while typing, so the way out belongs where they are looking.
          A daily cap is worded differently on purpose: it clears by itself
          tomorrow, so pushing them to pay would be selling them something they
          already have coming. */}
      {limitScope && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 bg-amber-500/10 border-t border-b border-amber-500/40">
          <div>
            <p className="text-amber-300 text-sm font-medium">
              {limitScope === 'daily'
                ? "You've used today's messages."
                : "You've used all your messages for this month."}
            </p>
            <p className="text-amber-200/70 text-xs mt-0.5">
              {limitScope === 'daily'
                ? 'Your daily allowance resets tomorrow. To keep going now, buy usage credits.'
                : 'Buy usage credits to keep going, or upgrade your plan.'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/credits')}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium whitespace-nowrap"
            >
              Buy usage credits
            </button>
            <button
              onClick={() => navigate('/subscribe')}
              className="px-3 py-2 rounded-lg text-amber-300 hover:text-amber-200 text-sm whitespace-nowrap"
            >
              Upgrade
            </button>
          </div>
        </div>
      )}

      {/* Chat Target Selector (AI Assistant + Webhooks) */}
      {chatTargets.length > 0 ? (
        <div className="webhook-selector">
          <label className="webhook-label">
            Select Workflow
          </label>
          <select
            value={selectedWebhook}
            onChange={(e) => setSelectedWebhook(e.target.value)}
            className="webhook-select"
            disabled={isLoading}
          >
            {chatTargets.map((target) => (
              <option key={target.id} value={target.id}>
                {target.name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="no-webhook-message">
          No webhook or AI provider configured. Add one in Settings.
        </div>
      )}

      {/* Voice Recording Input Section */}
      <VoiceRecordingInput onSendMessage={handleSendMessage} isLoading={isLoading} sttLang={settings?.voice?.sttLang || settings?.sttLang || 'en-US'} voiceEnabled={voiceEnabled} />
    </div>
  );
};

export default Chat;
